<template>
  <!-- Page Content -->
  <div
    class="d-flex flex-column"
    style="width: 100%; height: 35vh; position: relative;"
  >
    <!-- Row: title -->
    <div class="row f-row mb-1">
      <div class="markets-chart-title col col-12">
        <span style="color: var(--textactive)" v-if="isCurrency">
          {{ commodityDefs.slice(0, 3) }}/{{ commodityDefs.slice(3, 6) }}
        </span>
        <span style="color: var(--textactive)" v-else>
          {{ commodityDefs }}
        </span>
        VS
        <span style="color: var(--textactive)">
          {{ comparedAssetLong }}
        </span>
      </div>
    </div>

    <!-- Row: Chart -->
    <div class="row f-row row__chart-forecast">
      <div
        class="col col-12 text-center progress-div"
        style="top: 40%;"
        v-if="showLoadingAll || showLoadingHistorical"
      >
        <QSLoading
          :isLoading="showLoadingSpot"
          :size="40"
          :colorTheme="colorTheme"
        />
      </div>
      <div
        class="col col-12 text-center"
        v-else-if="chartDataRaw.length === 0"
        style="position: absolute; left: 0; top: 10.5%; z-index: 10000000;"
      >
        No Data Available
      </div>
      <div
        class="col col-12"
        style="height:100%; width:100%; position: relative;"
      >
        <!-- Row: legend -->
        <div
          class="row f-row"
          style="position: absolute; left: 50px; top: 5px; z-index: 0;"
        >
          <div class="col col-10 text-left">
            <table
              class="table table-sm table-responsive table-borderless table-market-legend"
              style="width: 100%; background: rgba(0,0,0,0)"
            >
              <tbody>
                <tr>
                  <td>
                    <i class="fa fa-minus mr-1" style="color:var(--expo)"></i
                    >{{ commodityDefs }}
                  </td>
                  <template v-if="comparedAssetLong">
                    <td>
                      <i
                        class="fa fa-minus mr-2"
                        style="color:var(--graylightbackground)"
                      ></i
                      >{{ comparedAssetLong }}
                    </td>
                    <td>
                      <i class="fa fa-minus mr-1" style="color:var(--fwd)"></i
                      >{{ comparedAssetLong }} Forecast
                    </td>
                  </template>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div
          class="h-100 d-flex flex-column"
          id="comparison-forecast-div"
          style="width: 100%;"
        >
          <svg id="svg-comparison-forecast"></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  colorExpo,
  colorFwdF,
  colorSymmetryH,
  textinactive,
  colorDarkExpo,
  grayLightBackground,
} from "@/_core/_helpers/_parametric_tools";
import { additionalYSpace } from "@/_core/_helpers/_helpers";
import { select } from "d3";
import { ComparisonHover } from "./hover.js";
import { Chart } from "@/_core/refactor/Chart";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "ComparisonForecast",
  components: {
    QSLoading,
  },
  props: {
    dataHistorical: { required: true, default: () => [] },
    dataForecast: { required: true, default: () => [] },
    chartDataRaw: { required: true },
    colorTheme: { required: true, type: Boolean, default: true },
    isCurrency: { required: true, type: Boolean, default: true },
    processData: { required: true },
    showLoadingHistorical: { required: true },
    showLoadingAll: { required: true },
    comparedAssetLong: { required: false, default: "" },
    commodityDefs: { required: true, default: "na" },
  },
  data() {
    return {
      loading: {
        local: false,
      },
      chartElements: {
        chartBodyTA: [],
        D3HoverFcstComp: null,
        D3Chart: null,
        svgNode: null,
      },
      settings: {
        colorExpo: colorExpo,
        modelColor: colorFwdF,
        historicalColor: colorSymmetryH,
        historicalSelectColor: textinactive,
        fieldUp: "",
        fieldUp2: "",
        dateFormat: "%d %b %y",
        currencyFormat: ",.4r",
      },
    };
  },
  mounted() {
    const _this = this;
    window.addEventListener("resize", function() {
      _this.createChart();
    });
  },
  watch: {
    colorTheme() {
      this.callWidth();
      this.createChart();
    },
    dataForecastFilter() {
      this.createChart();
    },
    "loading.local": {
      handler() {
        this.createChart();
      },
      deep: true,
    },
    showLoadingHistorical() {
      this.createChart();
    },
    showLoadingAll() {
      this.createChart();
    },
    commodityDefs() {
      this.createChart();
    },
    comparedAssetLong: {
      handler() {
        this.createChart();
      },
    },
  },
  computed: {
    dataForecastFilter() {
      this.updateSortedDataForecast();
      return this.dataForecast.filter((d) => d.name === this.settings.fieldUp2);
    },
    close() {
      return this.chartDataRaw.at(-1).close;
    },
  },
  created() {
    // ===== create initial charts instances =====
    this.chartElements.D3Chart = new Chart("comparison-forecast");
    this.loading.local = true;
  },
  methods: {
    deleteChart() {
      select("#comparison-forecast-div")
        .selectAll(".tooltip")
        .remove();
      select("#comparison-forecast-div")
        .select("svg > *")
        .remove();
    },
    addToDataForecast() {
      const newData = {
        date: new Date(),
        name: this.settings.fieldUp2,
        mean: this.dataHistorical.at(-1)[this.comparedAssetLong],
      };
      this.dataForecastFilter.unshift(newData);
    },
    updateSortedDataForecast() {
      this.dataForecast.sort((a, b) => +a.date - +b.date);
    },
    createChart() {
      if (this.chartDataRaw.length > 0) {
        this.loading.local = false;
        this.createHistChart();
      } else this.deleteChart();
    },
    createHistChart() {
      if (this.chartDataRaw.length === 0) return;

      // === set SVG canvas
      this.chartElements["D3Chart"].setSVG({
        top: { perc: 0.5, px: 9 },
        right: { perc: 0.4, px: 45 },
        bottom: { perc: 0.5, px: 20 },
        left: { perc: 0.4, px: 45 },
        colorTheme: this.colorTheme,
      });

      if (
        this.chartElements["D3Chart"].svg.height === 0 ||
        this.chartElements["D3Chart"].svg.width === 0
      ) {
        return;
      }
      // ==== zoom setup
      this.chartElements["D3Chart"].enableXZoom("chartBodyTA");

      const close = this.commodityDefs;
      const close2 = this.comparedAssetLong;
      // === minMax
      let minMaxYAxis, minMaxYAxis2;
      minMaxYAxis = this.getMinMaxYAxis(close, close2);
      minMaxYAxis2 = [minMaxYAxis[2], minMaxYAxis[3]];
      minMaxYAxis = [minMaxYAxis[0], minMaxYAxis[1]];

      if (this.dataForecastFilter.length > 0) {
        minMaxYAxis2 = this.processData.minMax(
          this.dataForecastFilter,
          "mean",
          "mean",
          minMaxYAxis2,
        );
        minMaxYAxis2 = additionalYSpace(minMaxYAxis2, 0.1);
        this.addToDataForecast();
      }

      this.chartElements["D3Chart"].setDoubleAxisDateLin({
        xRange: this.chartDataRaw
          .map((d) => d.date)
          .concat(
            this.dataForecastFilter.length > 0
              ? this.dataForecast.map((d) => d.date)
              : [],
          ),
        minMaxYAxis,
        minMax2YAxis: minMaxYAxis2,
        color1: this.settings.historicalColor(this.colorTheme),
        color2: this.settings.historicalSelectColor(this.colorTheme),
      });

      // Hovers
      this.chartElements["D3Chart"].addHovers({
        colorTheme: this.colorTheme,
        hLine: true,
        vLine: true,
      });

      this.chartElements["D3Chart"].createToolTipsHover(
        ["y1-2axis", "x", "y2-2axis"],
        {
          currencyFormat: this.settings.currencyFormat,
          colorTheme: this.colorTheme,
          xTooltipWidth: 60,
          yTooltipWidth: 40,
          colorY2AxisTooltip: grayLightBackground,
          colorYTooltip: colorDarkExpo,
        },
      );
      const createDots = (strokeColor, i) => {
        this.chartElements["D3Chart"].addDotHovers({
          type: "dot",
          strokeColor,
          i,
        });
      };
      createDots(colorDarkExpo(this.colorTheme), 0);
      createDots(grayLightBackground(this.colorTheme), 1);
      createDots(colorFwdF(this.colorTheme), 2);

      this.chartElements.svgNode = this.chartElements["D3Chart"].svg;
      this.twoAxes(close, close2);
      ComparisonHover(
        this,
        this.chartElements["D3Chart"],
        this.chartDataRaw,
        this.dataForecastFilter,
        this.dataHistorical,
      );
    },
    twoAxes(field1, field2) {
      this.settings.fieldUp = field1;
      this.settings.fieldUp2 = field2;
      // Line historical => close
      this.chartElements["D3Chart"].addLines({
        type: "line",
        data: this.chartDataRaw,
        color: this.settings.colorExpo(this.colorTheme),
        lineWidth: 1.5,
        fieldMame: "close",
        deltaBand: 0,
        xIsDate: true,
        fieldLine: "close",
      });
      // Line historical => "asset"
      this.chartElements["D3Chart"].addLines({
        type: "line2Axis",
        data: this.dataHistorical,
        color: this.settings.historicalSelectColor(this.colorTheme),
        lineWidth: 1.5,
        fieldMame: this.settings.fieldUp2,
        xIsDate: true,
        fieldLine: this.settings.fieldUp2,
      });
      // Line forecast
      this.chartElements["D3Chart"].addLines({
        type: "line2Axis",
        data: this.dataForecastFilter,
        color: this.settings.modelColor(this.colorTheme),
        lineWidth: 1.5,
        fieldMame: "mean",
        xIsDate: true,
        fieldLine: "mean",
      });
    },
    getMinMaxYAxis(close, close2) {
      let minMaxYAxis = [0, 1];
      let minMaxYAxis2 = [0, 1];
      // SAME AXIS
      minMaxYAxis = this.processData.minMax(
        this.chartDataRaw,
        "close",
        "close",
      );
      minMaxYAxis2 = this.processData.minMax(
        this.dataHistorical,
        close2,
        close2,
      );
      minMaxYAxis = additionalYSpace(minMaxYAxis, 0.1);
      minMaxYAxis2 = additionalYSpace(minMaxYAxis2, 0.1);
      return [minMaxYAxis[0], minMaxYAxis[1], minMaxYAxis2[0], minMaxYAxis2[1]];
    },
    callWidth() {
      this.deleteChart();
      if (this.chartDataRaw.length > 0) {
        this.createHistChart();
      }
    },
  },
};
</script>

<style>
.row__chart-forecast {
  height: 85%;
}
/* @media (min-height: 800px) {
  .row__chart-forecast {
    height: 80%;
  }
} */
</style>
